import { useEffect } from 'react';

// Links in the "How to use" boxes: switch to a tab, open a collapsed section
// and scroll to the element the step talks about.

const EVENT = 'client-reports:goto';

// A tab mounts only when first opened, so it cannot hear the event that
// switched to it; it picks the section up from here instead.
let pending = null;

const flash = (element, color) => {
  const { outline, outlineOffset, transition } = element.style;
  element.style.transition = 'outline-color 0.4s';
  element.style.outline = `2px solid ${color}`;
  element.style.outlineOffset = '4px';
  setTimeout(() => {
    element.style.outline = outline;
    element.style.outlineOffset = outlineOffset;
    element.style.transition = transition;
  }, 1600);
};

/**
 * Go to the place a step talks about.
 *
 * @param {Object} target - { tab, section?, id }
 * @param {string} color - Highlight colour
 * @param {string} fromTab - The tab the link is on
 */
export const goTo = (target, color, fromTab) => {
  pending = target.tab !== fromTab ? target : null;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: target }));
  // The element appears once the tab and the section have rendered; the
  // section's opening animation takes about 0.2 s.
  let tries = 0;
  const find = () => {
    const element = document.getElementById(target.id);
    if (element) {
      // A tall section is shown from its top, a button in the middle. Again
      // while the tab and the section finish their opening animations, which
      // keep moving it.
      const scroll = () =>
        element.scrollIntoView({
          block: element.offsetHeight > window.innerHeight / 2 ? 'start' : 'center',
        });
      [0, 150, 400, 800].forEach((ms) => setTimeout(scroll, ms));
      flash(element, color);
      return;
    }
    tries += 1;
    if (tries < 20) setTimeout(find, 100);
  };
  setTimeout(find, 300);
};

/**
 * Switch tabs when a link points to another tab.
 *
 * @param {Function} setTab - Receives the tab key
 */
export const useGoToTab = (setTab) => {
  useEffect(() => {
    const onGoTo = (e) => {
      if (e.detail?.tab) setTab(e.detail.tab);
    };
    window.addEventListener(EVENT, onGoTo);
    return () => window.removeEventListener(EVENT, onGoTo);
  }, [setTab]);
};

/**
 * Open a collapsed section of this tab when a link points into it.
 *
 * @param {string} tab - This tab's key
 * @param {Function} openSection - Receives the section key
 */
export const useGoToSection = (tab, openSection) => {
  useEffect(() => {
    if (pending?.tab === tab && pending.section) {
      openSection(pending.section);
      pending = null;
    }
    const onGoTo = (e) => {
      const { tab: target, section } = e.detail || {};
      // After the tab switch has shown this tab: a section opened while its
      // tab is hidden stays stuck at zero height.
      if (section && target === tab) setTimeout(() => openSection(section), 60);
    };
    window.addEventListener(EVENT, onGoTo);
    return () => window.removeEventListener(EVENT, onGoTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
};
